import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  CircularProgress,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@icons/ArrowBackIcon";
import { styled } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { isLocalAuthenticated, isNoOrgAccountSession } from "@services/auth";
import {
  createCheckoutSession,
  getDiscountCodes,
  getPricingTiers,
  getPricingTiersWithRegionalFallback,
  getAnnualPlanComparison,
  PricingTier,
  createUserPreferredPlan,
  checkUserFacilityAssociation,
  getUserPreferredFacilityById,
} from "@services/payment";
import { invalidateMyFacilitiesCache } from "@services/facilityServices";
import { prewarmPostCheckoutCompliance } from "@services/postCheckoutCompliancePrewarm";
import { syncPreviewVerticalAfterPurchase } from "@services/verticals";
import {
  POST_PURCHASE_DEFAULT_ACTIVE_TAB,
  POST_PURCHASE_FACILITY_SELECTION_KEY,
} from "@utils/postPurchaseFacilitySelection";
import {
  getRoutePreferredFacilityId,
  readPreferredFacilityForPricing,
  resolveCheckoutFacilityType,
  resolveCheckoutFacilityTypeId,
  resolveCheckoutProfileSlug,
  resolveCheckoutTaxonomy,
  toFiniteNumber,
} from "@utils/pricingFacilityContext";
import {
  filterFacilityPurchasePricingTiers,
  matchCatalogFacilityTypeId,
  sameSelectPlanFacilitySnapshot,
  selectPlanFacilityHintFromContext,
  selectPlanPricingRequestKey,
} from "@utils/selectPlanPricing";
import { withSnfCheckoutType } from "@utils/snfCheckoutType";
import { getFacilityTypes } from "@services/facilitytype";
import {
  getSelectedCountryCode,
  getPricingLocaleFiltersForApi,
} from "@utils/countryUtils";
import OrderSummary from "@screens/Pricing/OrderSummary/OrderSummary";
import { toast } from "react-toastify";
import { getCurrentUser, userIsSalesUser } from "@utils/userUtils";
import { isSalesOnlyDynamicBypassCode } from "@utils/salesBypassCode";
import {
  leftoverFacilitiesMatch,
  leftoverFacilityId,
  leftoverFacilityNpi,
  leftoverPreferredPk,
  markPromoCheckoutClearedRestricted,
  persistLeftoverUnpaidIdentity,
  persistPreferredFacilityForSelectPlan,
  pickLeftoverUnpaidSource,
  readLeftoverUnpaidIdentity,
  resolveLeftoverPayNowNpi,
  resolveLeftoverPayNowPromo,
  shouldClearAccessRestrictedAfterPromoPayNow,
} from "@utils/leftoverUnpaidCheckout";
import {
  isSelfReportedPreferredFacility,
  resolveCheckoutPreferredFacilityPk,
  selfReportedFacilityTypeSlug,
  selfReportedLicenseNumber,
  selfReportedUniqueId,
  SELF_REPORTED_VERIFICATION_SOURCE,
} from "@utils/selfReportedPreferredFacility";
import { fetchAndMergeAdminPromoForLeftover } from "@utils/leftoverUnpaidPromo";
import { associationUniqueIdFromLookupFacility } from "@utils/facilityUtils";

/** Checkout metadata never sends null — payload type is optional / undefined. */
function checkoutMetadataValue<T>(value: T): Exclude<T, null> | undefined {
  return value == null ? undefined : (value as Exclude<T, null>);
}

type PostPurchaseFacilityState = {
  currentView: "MyFacility";
  activeTab: typeof POST_PURCHASE_DEFAULT_ACTIVE_TAB;
  facilityId?: number;
  facilityName?: string;
  facilityCity?: string;
  /** Purchased facility vertical (e.g. aviation) for preview sync + list filter. */
  vertical?: string;
  facilityTypeSlug?: string;
};

const buildPostPurchaseFacilityState = (
  preferredFacilityForPricing?: any,
  fallbackFacility?: any,
  /** Authoritative Rules `facility_id` from POST /stripe/create-checkout-session (bypass). */
  checkoutRulesFacilityId?: number,
): PostPurchaseFacilityState => {
  const preferredDetails = preferredFacilityForPricing?.facility_details ?? {};
  const fromCheckout =
    typeof checkoutRulesFacilityId === "number" &&
    Number.isFinite(checkoutRulesFacilityId)
      ? checkoutRulesFacilityId
      : undefined;
  const fromDetails =
    preferredDetails?.facility_id != null &&
    Number.isFinite(Number(preferredDetails.facility_id))
      ? Number(preferredDetails.facility_id)
      : undefined;
  const fromPreferredRoot =
    preferredFacilityForPricing?.facility_id != null &&
    Number.isFinite(Number(preferredFacilityForPricing.facility_id))
      ? Number(preferredFacilityForPricing.facility_id)
      : undefined;
  const fromFallback =
    fallbackFacility?.facility_id != null &&
    Number.isFinite(Number(fallbackFacility.facility_id))
      ? Number(fallbackFacility.facility_id)
      : undefined;
  const facilityId =
    fromCheckout ?? fromDetails ?? fromPreferredRoot ?? fromFallback;
  const facilityName =
    preferredDetails?.facility_name ??
    preferredDetails?.site_name ??
    preferredFacilityForPricing?.facility_name ??
    fallbackFacility?.facility_name ??
    fallbackFacility?.site_name;
  const facilityCity =
    preferredDetails?.city ??
    preferredFacilityForPricing?.city ??
    fallbackFacility?.city;
  const verticalRaw =
    preferredDetails?.vertical ??
    preferredFacilityForPricing?.vertical ??
    fallbackFacility?.vertical;
  const vertical =
    typeof verticalRaw === "string" && verticalRaw.trim()
      ? verticalRaw.trim().toLowerCase()
      : undefined;
  const slugRaw =
    preferredDetails?.facility_type_slug ??
    preferredFacilityForPricing?.facility_type_slug ??
    fallbackFacility?.facility_type_slug;
  const facilityTypeSlug =
    typeof slugRaw === "string" && slugRaw.trim()
      ? slugRaw.trim().toLowerCase()
      : undefined;

  return {
    currentView: "MyFacility",
    activeTab: POST_PURCHASE_DEFAULT_ACTIVE_TAB,
    ...(typeof facilityId === "number" ? { facilityId } : {}),
    ...(facilityName ? { facilityName } : {}),
    ...(facilityCity ? { facilityCity } : {}),
    ...(vertical ? { vertical } : {}),
    ...(facilityTypeSlug ? { facilityTypeSlug } : {}),
  };
};

const PricingSection = styled(Box)({
  // background:
  //   "linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #E9FBFB 69.71%, rgba(255, 255, 255, 0.00) 100%)",
  padding: "64px 0",
  display: "flex",
  // padding: "16px 0",
  justifyContent: "center",
  alignItems: "flex-end",
  gap: "24px",
  alignSelf: "stretch",
  borderRadius: "12px",
  border: "1px solid var(--Primary-Color-Palette-300, #99D4E1)",
  background: "#FDFFFF",
  boxShadow: "8px 2px 58.4px 0 rgba(191, 242, 255, 0.20) inset",
});

const PricingCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isPopular",
})<{ isPopular?: boolean }>(({ isPopular = false }) => ({
    position: "relative",
    backgroundColor: "#ffffff",
    borderRadius: 8,
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    height: "92%",
    minHeight: "480px",
    border: isPopular ? "2px solid #c3f4f8" : "2px solid #e3f2fd",
    boxShadow:
      "0px 2px 19.4px rgba(118, 223, 247, 0.3), 0px 1px 2px rgba(0, 0, 0, 0.05)",
    justifyContent: "flex-start",
  }));

const PopularBadge = styled(Box)({
  position: "absolute",
  top: -14,
  left: 36,
  background:
    "var(--Gradlient-1, linear-gradient(94deg, var(--Brand-Colors-color--grad-1, #35D7E4) -2.59%, var(--Primary-Color-Palette-500, #0A829D) 115.51%))",
  //   backgroundColor: "#065b70",
  color: "var(--White, var(--White, #FFF))",
  padding: "6px 16px",
  borderRadius: 8,
  fontFamily: "Roboto",
  fontSize: 12,
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "16px",
  letterSpacing: "0.3px",
  textTransform: "uppercase",
});

const FeatureItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSpecial",
})<{ isSpecial?: boolean }>(({ isSpecial = false }) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    marginBottom: 24,
  }));

const FeatureIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSpecial",
})<{ isSpecial?: boolean }>(({ isSpecial = false }) => ({
    width: 24,
    height: 24,
    flexShrink: 0,
    "& svg": {
      width: "100%",
      height: "100%",
      fill: isSpecial ? "#ffd700" : "#10b981",
    },
  }));

const BillingToggle = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "6px 14px",
  minHeight: "38px",
  borderRadius: "30px",
  border: "1px solid #9AD5E2",
  background: "#E8F6FA",
  overflow: "hidden",
});

// Check icon SVG
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

// Star icon SVG
const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

// Pricing icon SVG

interface FeatureItemProps {
  text: string;
  isSpecial?: boolean;
}

function FeatureItemComponent({ text, isSpecial = false }: FeatureItemProps) {
  return (
    <FeatureItem isSpecial={isSpecial}>
      <FeatureIcon isSpecial={isSpecial}>
        {isSpecial ? <StarIcon /> : <CheckIcon />}
      </FeatureIcon>
      <Typography
        sx={{
          fontWeight: 400,
          color: "#6b7280",
          fontSize: 16,
          lineHeight: "24px",
          flex: 1,
        }}
      >
        {text}
      </Typography>
    </FeatureItem>
  );
}

interface PricingCardProps {
  title: string;
  price: string;
  period?: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  buttonText: string;
  buttonVariant: "primary" | "secondary";
  specialFeatureIndex?: number;
  id: string;
  onSelect: (id: string) => void;
  onCardSelect?: (id: string) => void;
  onHoverChange?: (id: string | null) => void;
  isHovered?: boolean;
  isSelected: boolean;
}

function PricingCardComponent({
  title,
  price,
  period,
  description,
  features,
  isPopular = false,
  buttonText,
  buttonVariant,
  specialFeatureIndex,
  id,
  onSelect,
  onCardSelect,
  onHoverChange,
  isHovered = false,
  isSelected,
}: PricingCardProps) {
  const showActionButton = isSelected || isHovered;

  return (
    <PricingCard
      isPopular={isPopular}
      onClick={() => onCardSelect?.(id)}
      onMouseEnter={() => onHoverChange?.(id)}
      onMouseLeave={() => onHoverChange?.(null)}
      sx={{
        cursor: "pointer",
        border: isSelected ? "2px solid #0A829D" : undefined,
        transform: isSelected ? "scale(1.02)" : undefined,
        transition: "all 0.2s ease-in-out",
      }}
    >
      {isPopular && <PopularBadge>Most Popular</PopularBadge>}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          position: "relative",
        }}
      >
        {/* Header */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography
            sx={{
              color: "#0A829D",
              fontSize: "20px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "28px",
              mb: 2,
            }}
          >
            {title}
          </Typography>

          {/* Price */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexShrink: 0,
              minHeight: "44px",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                color: "var(--Gray-6, var(--Gray-5, #101828))",
                fontSize: "35px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "42px",
                letterSpacing: "-0.96px",
              }}
            >
              {price}
            </Typography>
            {period && (
              <Typography
                sx={{
                  color: "var(--Gray-6, var(--Gray-5, #101828))",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "18px",
                  mt: "6px",
                  mb: 0,
                }}
              >
                / {period}
              </Typography>
            )}
          </Box>

          {/* Description hidden per latest UI requirement */}
        </Box>

        {/* Features List - Fixed height container for alignment */}
        {showActionButton && (
          <Button
            type="button"
            onClick={() => onSelect(id)}
            disabled={!isSelected}
            sx={{
              mb: 2,
              alignSelf: "stretch",
              minHeight: 52,
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              px: 1.5,
              py: 0.75,
              background: "linear-gradient(94deg, #35D7E4 -2.59%, #0A829D 115.51%)",
              color: "#ffffff",
              boxShadow: "none",
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
              "&:hover": {
                background: "linear-gradient(94deg, #2ec6d3 -2.59%, #09768f 115.51%)",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                background: "#9FB4BA",
                color: "#EAF4F7",
              },
            }}
          >
            {buttonText}
          </Button>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: "200px",
            paddingRight: 2,
          }}
        >
          {features.map((feature, index) => (
            <FeatureItemComponent key={index} text={feature} />
          ))}
        </Box>
      </Box>
    </PricingCard>
  );
}

export default function UniComply_Pricing() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMonthly, setIsMonthly] = useState<boolean>(
    () => !!(location.state as any)?.isMonthly
  );
  const fromFacilityValidation = !!(location.state as any)?.fromFacilityValidation;
  /** Expired/cancelled Access Restricted → Update Payment: do not auto-apply old promo. */
  const fromRestrictedUpdatePayment = !!(location.state as any)
    ?.fromRestrictedUpdatePayment;
  const [annualSavePercent, setAnnualSavePercent] = useState<number | null>(null);
  const isOrderSummaryPage = location.pathname === "/dashboard/pricing/order-summary";

  // Get user email from localStorage
  const [userEmail, setUserEmail] = useState<string>("");
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  // Stores the plan name to match after billing cycle toggle causes tiers to reload
  const pendingMatchNameRef = React.useRef<string | null>(null);
  // Persists the last explicitly selected plan name so switching back restores it even when no match was found in the other cycle
  const lastSelectedPlanNameRef = React.useRef<string | null>(null);
  const [hoveredPlanId, setHoveredPlanId] = useState<string | null>(null);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [userPlan, setPlan] = useState<string>("");
  const [userName, setName] = useState<string>("");

  const readFilteredSelectedFacility = () => {
    const stored = sessionStorage.getItem("filtered_selectedFacility");
    try {
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      console.error("Invalid JSON in sessionStorage:", stored, err);
      return null;
    }
  };
  const [filtered_selectedFacility, setFilteredSelectedFacility] = useState(
    () => {
      return readFilteredSelectedFacility();
    }
  );

  // Track facility ID to detect changes
  const [currentFacilityId, setCurrentFacilityId] = useState<number | null>(null);

  const pricingReloadToken = (location.state as any)?.pricingReloadToken;
  const routePreferredFacilityId = getRoutePreferredFacilityId(location.state as any);

  // Watch for facility changes and clear selection
  useEffect(() => {
    const checkFacilityChange = () => {
      try {
        // Check preferred_facility_for_pricing
        const parsed = readPreferredFacilityForPricing(routePreferredFacilityId);
        if (parsed) {
          const newFacilityId = parsed.id;
          
          if (currentFacilityId !== null && currentFacilityId !== newFacilityId) {
            // Facility changed - clear selections and reset toggle to Annual
            console.log("🔄 Facility changed, clearing plan selection and order summary");
            setSelectedPlanId(null);
            setIsMonthly(false);
            lastSelectedPlanNameRef.current = null;
            pendingMatchNameRef.current = null;
          }
          
          setCurrentFacilityId(newFacilityId);
        } else if (filtered_selectedFacility?.id) {
          const newFacilityId = filtered_selectedFacility.id;
          
          if (currentFacilityId !== null && currentFacilityId !== newFacilityId) {
            // Facility changed - clear selections and reset toggle to Annual
            console.log("🔄 Facility changed, clearing plan selection and order summary");
            setSelectedPlanId(null);
            setIsMonthly(false);
            lastSelectedPlanNameRef.current = null;
            pendingMatchNameRef.current = null;
          }
          
          setCurrentFacilityId(newFacilityId);
        }
      } catch (err) {
        console.error("Error checking facility change:", err);
      }
    };

    checkFacilityChange();
    
    // Poll for facility changes
    const interval = setInterval(checkFacilityChange, 500);
    
    return () => clearInterval(interval);
  }, [currentFacilityId, filtered_selectedFacility, routePreferredFacilityId]);

  // Refresh selected facility snapshot when Dashboard routes back into Pricing.
  // Keep the same object when JSON.parse only changes identity (AFH type id null).
  useEffect(() => {
    setFilteredSelectedFacility((prev: any) => {
      const next = readFilteredSelectedFacility();
      return sameSelectPlanFacilitySnapshot(prev, next) ? prev : next;
    });
  }, [location.search, pricingReloadToken, routePreferredFacilityId]);
  const [npiNumber, setNpiNumber] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  useEffect(() => {
    const stored = sessionStorage.getItem("sessionNpiNumber");
    if (stored) {
      setNpiNumber(stored);
      console.log("NPI Number from session:", stored);
    }
  }, []); // runs once on mount

  useEffect(() => {
    const stored = sessionStorage.getItem("sessionPhoneNumber");
    if (stored) {
      setPhoneNumber(stored);
      console.log("Phone Number from session:", stored);
    }
  }, []); // runs once on mount

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      const user = JSON.parse(userData);
      setPlan(user?.user_preferred_plan?.plan_name);
      console.log("Plan Name:", user?.user_preferred_plan?.plan_name);
    }
  }, []);

  // State for URL params handling
  const [promoCodeFromUrl, setPromoCodeFromUrl] = useState<string | null>(null);
  const [facilityIdFromUrl, setFacilityIdFromUrl] = useState<number | null>(null);
  const [facilityLoadedFromUrl, setFacilityLoadedFromUrl] = useState(false);
  const lastPricingRequestKeyRef = React.useRef<string | null>(null);
  const inflightPricingRequestKeyRef = React.useRef<string | null>(null);

  const selectedFacilityRecordId = toFiniteNumber(
    filtered_selectedFacility?.id ?? filtered_selectedFacility?.facility_id,
  );
  const selectedFacilityTypeId = toFiniteNumber(
    filtered_selectedFacility?.facility_type_id,
  );
  const selectedFacilitySlug = String(
    filtered_selectedFacility?.primary_profile_slug ||
      filtered_selectedFacility?.facility_type_slug ||
      "",
  ).trim();
  const selectedFacilityVertical = String(
    filtered_selectedFacility?.vertical || "",
  ).trim();

  // Keep selected plan in URL so the order-summary page can be opened directly.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const selectedPlanFromUrl = params.get("selected_plan_id");
    if (selectedPlanFromUrl) {
      setSelectedPlanId(selectedPlanFromUrl);
    }
  }, [location.search]);

  const handleBackToFacilityValidation = () => {
    navigate("/dashboard/facilities", {
      state: { currentView: "facilityValidation" },
    });
  };

  // When coming from Dashboard (My Facility - Pending/Active with discount), read discount from preferred_facility_for_pricing.
  // Skip when Access Restricted → Update Payment (user must type/apply promo themselves).
  React.useEffect(() => {
    if (fromRestrictedUpdatePayment) return;
    const searchParams = new URLSearchParams(location.search);
    const promoFromUrl = searchParams.get("promo_code");
    if (promoFromUrl) return; // URL already has promo, use that
    try {
      const parsed = readPreferredFacilityForPricing(routePreferredFacilityId);
      if (!parsed) return;
      const discountCoupon = parsed?.discount_coupon_name;
      if (discountCoupon && typeof discountCoupon === "string" && discountCoupon.trim()) {
        setPromoCodeFromUrl(discountCoupon.trim());
      }
    } catch (_) {
      // ignore
    }
  }, [
    location.search,
    pricingReloadToken,
    routePreferredFacilityId,
    fromRestrictedUpdatePayment,
  ]);

  // AB#10916 follow-up: leftover Mackay promo is on the admin/preferred row.
  // Hydrate it so Order Summary Pay Now bypasses even when the picker row
  // omitted discount_coupon_name.
  React.useEffect(() => {
    if (fromRestrictedUpdatePayment) return;
    let cancelled = false;
    const hydrateLeftoverAdminPromo = async () => {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get("promo_code")) return;
      const parsed = readPreferredFacilityForPricing(routePreferredFacilityId);
      const leftoverStored = readLeftoverUnpaidIdentity();
      // Multi-pending: do not overwrite clicked facility with sibling leftover.
      if (
        parsed &&
        leftoverStored &&
        !leftoverFacilitiesMatch(leftoverStored, parsed)
      ) {
        const ownPromo = String(parsed?.discount_coupon_name ?? "").trim();
        if (ownPromo) setPromoCodeFromUrl(ownPromo);
        return;
      }
      const leftover = pickLeftoverUnpaidSource(
        leftoverStored,
        parsed,
        filtered_selectedFacility,
      );
      if (!leftover) return;
      const already = String(
        leftover?.discount_coupon_name ?? parsed?.discount_coupon_name ?? "",
      ).trim();
      if (already && leftoverFacilityNpi(leftover).length === 10) {
        persistLeftoverUnpaidIdentity(leftover);
        persistPreferredFacilityForSelectPlan(leftover);
        setFilteredSelectedFacility(readFilteredSelectedFacility());
        setPromoCodeFromUrl(already);
        return;
      }
      const merged = await fetchAndMergeAdminPromoForLeftover(leftover);
      if (cancelled || !merged) return;
      persistLeftoverUnpaidIdentity(merged);
      persistPreferredFacilityForSelectPlan(merged);
      setFilteredSelectedFacility(readFilteredSelectedFacility());
      const promo = String(merged.discount_coupon_name ?? "").trim();
      if (promo) setPromoCodeFromUrl(promo);
    };
    void hydrateLeftoverAdminPromo();
    return () => {
      cancelled = true;
    };
  }, [
    location.search,
    pricingReloadToken,
    routePreferredFacilityId,
    selectedFacilityRecordId,
    fromRestrictedUpdatePayment,
  ]);

  // Load pricing tiers and user email; refetch on billing toggle
  // and when Dashboard passes a new pricingReloadToken in route state (active facility click)
  // Also reload when facilityIdFromUrl changes (facility fetched from URL params)
  React.useEffect(() => {
    // If we're waiting for a facility from URL, don't load pricing tiers yet
    const searchParams = new URLSearchParams(location.search);
    const facilityIdParam = searchParams.get("user_preferred_facility_id");
    if (facilityIdParam && !facilityLoadedFromUrl) {
      console.log("⏳ Waiting for facility to load before fetching pricing tiers...");
      return;
    }
    
    const loadData = async () => {
      const locale = getPricingLocaleFiltersForApi();
      let ardcode = "";
      try {
        ardcode = sessionStorage.getItem("ardcode") || "";
      } catch (_) {}
      const preferredForKey = readPreferredFacilityForPricing(routePreferredFacilityId);
      const leftoverForKey = readLeftoverUnpaidIdentity();
      const hintForKey = selectPlanFacilityHintFromContext(
        preferredForKey,
        leftoverForKey || filtered_selectedFacility,
      );
      const planIdFromUrl = new URLSearchParams(location.search).get("selected_plan_id");
      const directPlanId = Number(planIdFromUrl);
      const requestKey = selectPlanPricingRequestKey({
        frequency: isMonthly ? "monthly" : "yearly",
        facilityTypeId: hintForKey.id ?? selectedFacilityTypeId,
        profileSlug: hintForKey.slug || selectedFacilitySlug,
        vertical:
          preferredForKey?.vertical ||
          preferredForKey?.facility_details?.vertical ||
          selectedFacilityVertical ||
          "healthcare",
        currency: locale.currency,
        ardcode,
        preferredFacilityId:
          toFiniteNumber(preferredForKey?.id) ??
          routePreferredFacilityId ??
          selectedFacilityRecordId,
        directPlanId:
          isOrderSummaryPage && Number.isFinite(directPlanId) && directPlanId > 0
            ? directPlanId
            : null,
      });
      if (
        lastPricingRequestKeyRef.current === requestKey ||
        inflightPricingRequestKeyRef.current === requestKey
      ) {
        return;
      }
      inflightPricingRequestKeyRef.current = requestKey;

      try {
        // Load user email
        const userData = localStorage.getItem("user");
        if (userData) {
          const user = JSON.parse(userData);
          console.log("User:", user);
          setUserEmail(user.email || "");
          setName(user.first_name);
        }

        // Load pricing tiers
        setLoading(true);

        // Order Summary already has the facility plan id. Load that plan directly.
        // Filtered catalog (facility_type / frequency / profile) is for Select Plan only.
        if (isOrderSummaryPage && Number.isFinite(directPlanId) && directPlanId > 0) {
          let directVertical: string | undefined;
          try {
            const pref = readPreferredFacilityForPricing(routePreferredFacilityId);
            directVertical =
              pref?.vertical ??
              pref?.facility_details?.vertical ??
              pref?.facility?.vertical ??
              (filtered_selectedFacility as any)?.vertical;
          } catch (_) {}
          const direct = await getPricingTiers({
            id: directPlanId,
            sku_name: "SKU.Unicomply.Rules",
            ...(directVertical ? { vertical: String(directVertical) } : {}),
          });
          if (direct.success && Array.isArray(direct.data) && direct.data.length > 0) {
            setPricingTiers(direct.data);
            lastPricingRequestKeyRef.current = requestKey;
            const freq = String(direct.data[0]?.frequency_name || "").toLowerCase();
            if (freq.includes("month")) setIsMonthly(true);
            else if (freq.includes("year")) setIsMonthly(false);
            return;
          }
          // Direct lookup found nothing — fall back to the filtered catalog below
          // instead of leaving Order Summary with no plan.
        }

        const filters: any = {
          frequency: isMonthly ? "monthly" : "yearly",
          sku_name: "SKU.Unicomply.Rules",
          ...getPricingLocaleFiltersForApi(),
        };

        // Resolve facility_type_id for pricing tiers:
        // 1) Prefer the selected active preferred facility (from user-preferred-facilities API)
        // 2) Fallback to filtered_selectedFacility (from validation flow)
        let facilityTypeIdForPricing: number | undefined;
        let verticalForPricing: string | undefined;
        let primaryProfileSlugForPricing: string | undefined;
        try {
          const parsedPreferred = readPreferredFacilityForPricing(routePreferredFacilityId);
          console.log("🔍 Checking preferred facility for pricing:", parsedPreferred);
          if (parsedPreferred) {
            console.log("📋 Parsed facility from sessionStorage:", parsedPreferred);
            
            // Try multiple locations for facility_type_id
            const fromDetails = parsedPreferred?.facility_details?.facility_type_id;
            const fromRoot = parsedPreferred?.facility_type_id;
            const leftoverForType = readLeftoverUnpaidIdentity();
            const inferredTypeId = resolveCheckoutFacilityTypeId(
              parsedPreferred,
              leftoverForType || filtered_selectedFacility,
            );
            const inferredSlug = resolveCheckoutProfileSlug(
              parsedPreferred,
              leftoverForType || filtered_selectedFacility,
            );
            if (inferredSlug) {
              primaryProfileSlugForPricing = inferredSlug;
            }
            const resolved = inferredTypeId ?? fromRoot ?? fromDetails;
            const details = parsedPreferred?.facility_details ?? {};
            const targeting =
              details?.profile_targeting_json ??
              parsedPreferred?.profile_targeting_json ??
              {};
            verticalForPricing =
              parsedPreferred?.vertical ??
              details?.vertical ??
              parsedPreferred?.facility?.vertical;
            primaryProfileSlugForPricing =
              inferredSlug ||
              parsedPreferred?.primary_profile_slug ||
              details?.primary_profile_slug ||
              targeting?.primary_profile_slug ||
              parsedPreferred?.facility?.primary_profile_slug;
            
            console.log("🔎 facility_type_id candidates:", {
              fromRoot,
              fromDetails,
              resolved,
              verticalForPricing,
              primaryProfileSlugForPricing,
            });
            
            if (typeof resolved === "number") {
              facilityTypeIdForPricing = resolved;
            } else if (
              typeof resolved === "string" &&
              resolved.trim() !== "" &&
              !Number.isNaN(Number(resolved))
            ) {
              facilityTypeIdForPricing = Number(resolved);
            }
            
            console.log("✅ Final facility_type_id for pricing:", facilityTypeIdForPricing);
          } else {
            console.log("⚠️ No facility found in sessionStorage");
          }
        } catch (error) {
          console.error("❌ Error parsing facility from sessionStorage:", error);
        }

        if (
          !facilityTypeIdForPricing &&
          primaryProfileSlugForPricing !== "skilled_nursing_facility" &&
          filtered_selectedFacility?.facility_type_id
        ) {
          const raw = filtered_selectedFacility.facility_type_id as any;
          if (typeof raw === "number") {
            facilityTypeIdForPricing = raw;
          } else if (
            typeof raw === "string" &&
            raw.trim() !== "" &&
            !Number.isNaN(Number(raw))
          ) {
            facilityTypeIdForPricing = Number(raw);
          }
        }

        if (!verticalForPricing && filtered_selectedFacility?.vertical) {
          verticalForPricing = String(filtered_selectedFacility.vertical);
        }
        if (
          !primaryProfileSlugForPricing &&
          (filtered_selectedFacility as any)?.primary_profile_slug
        ) {
          primaryProfileSlugForPricing = String(
            (filtered_selectedFacility as any).primary_profile_slug,
          );
        }
        if (
          !primaryProfileSlugForPricing &&
          (filtered_selectedFacility as any)?.facility_type_slug
        ) {
          primaryProfileSlugForPricing = String(
            (filtered_selectedFacility as any).facility_type_slug,
          );
        }

        if (!facilityTypeIdForPricing && primaryProfileSlugForPricing?.trim()) {
          try {
            const catalog = await getFacilityTypes();
            const resolvedId = matchCatalogFacilityTypeId(
              catalog,
              primaryProfileSlugForPricing,
            );
            if (resolvedId) {
              facilityTypeIdForPricing = resolvedId;
            }
          } catch (error) {
            console.warn(
              "⚠️ Could not resolve facility_type_id from catalog:",
              error,
            );
          }
        }

        if (typeof facilityTypeIdForPricing === "number") {
          filters.facility_type = facilityTypeIdForPricing;
          console.log("✅ Adding facility_type to pricing filters:", filters);
        } else {
          console.log("⚠️ No facility_type_id found, pricing filters:", filters);
        }

        if (verticalForPricing?.trim()) {
          filters.vertical = verticalForPricing.trim();
        } else {
          filters.vertical = "healthcare";
        }
        if (primaryProfileSlugForPricing?.trim()) {
          filters.primary_profile_slug = primaryProfileSlugForPricing.trim();
        }

        try {
          const ardcode = sessionStorage.getItem("ardcode");
          if (ardcode) filters.ardcode = ardcode;
        } catch (_) {}

        console.log("📤 Calling getPricingTiers with filters:", filters);
        const response = await getPricingTiersWithRegionalFallback(filters);
        console.log("📥 Pricing tiers response:", response);

        if (response.success) {
          const leftoverForHint = readLeftoverUnpaidIdentity();
          const hint = selectPlanFacilityHintFromContext(
            readPreferredFacilityForPricing(routePreferredFacilityId),
            leftoverForHint || filtered_selectedFacility,
          );
          if (typeof facilityTypeIdForPricing === "number") {
            hint.id = facilityTypeIdForPricing;
          }
          if (primaryProfileSlugForPricing?.trim()) {
            hint.slug = primaryProfileSlugForPricing.trim();
          }
          setPricingTiers(
            filterFacilityPurchasePricingTiers(response.data, hint),
          );
          lastPricingRequestKeyRef.current = requestKey;
        } else {
          toast.error("Failed to load pricing data");
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load pricing data");
      } finally {
        if (inflightPricingRequestKeyRef.current === requestKey) {
          inflightPricingRequestKeyRef.current = null;
        }
        setLoading(false);
      }
    };

    loadData();

    let lastCountry = getSelectedCountryCode();
    const countryPoll = setInterval(() => {
      const now = getSelectedCountryCode();
      if (now !== lastCountry) {
        lastCountry = now;
        loadData();
      }
    }, 500);

    return () => clearInterval(countryPoll);
  }, [
    isMonthly,
    isOrderSummaryPage,
    facilityIdFromUrl,
    facilityLoadedFromUrl,
    location.search,
    routePreferredFacilityId,
    selectedFacilityRecordId,
    selectedFacilityTypeId,
    selectedFacilitySlug,
    selectedFacilityVertical,
  ]);

  // Handle URL params: user_preferred_facility_id and promo_code
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const facilityIdParam = searchParams.get("user_preferred_facility_id");
    const promoCodeParam = searchParams.get("promo_code");

    if (facilityIdParam) {
      const facilityId = parseInt(facilityIdParam, 10);
      if (!isNaN(facilityId)) {
        setFacilityIdFromUrl(facilityId);
        setFacilityLoadedFromUrl(false);
        // Fetch facility details from API
        const fetchFacilityDetails = async () => {
          try {
            console.log("🔍 Fetching facility details for ID:", facilityId);
            const response = await getUserPreferredFacilityById(facilityId);
            console.log("✅ Facility API response:", response);
            if (response.success && response.data) {
              const facility = response.data;
              console.log("📦 Facility data:", facility);
              
              // Extract facility_type_id from facility_details (same as drawer behavior)
              const facilityTypeId = facility.facility_details?.facility_type_id || null;
              
              console.log("🏥 Extracted facility_type_id:", facilityTypeId);
              
              // Store facility in sessionStorage exactly like drawer does
              const facilityDetails = facility.facility_details as any || {};
              const facilityToStore = withSnfCheckoutType({
                id: facility.id,
                user_id: facility.user_id,
                is_verified: facility.is_verified,
                is_active: facility.is_active,
                facility_name: facility.facility_name || facilityDetails?.facility_name || "",
                facility_type: facility.facility_type || facilityDetails?.facility_type || "",
                facility_type_id: facilityTypeId, // Store at root level for easy access
                vertical: (facility as any).vertical || facilityDetails?.vertical || null,
                primary_profile_slug:
                  (facility as any).primary_profile_slug ||
                  facilityDetails?.primary_profile_slug ||
                  facilityDetails?.profile_targeting_json?.primary_profile_slug ||
                  null,
                profile_targeting_json:
                  facilityDetails?.profile_targeting_json ||
                  (facility as any).profile_targeting_json ||
                  null,
                discount_coupon_name: facility.discount_coupon_name || null,
                discount_end_date: facility.discount_end_date || null,
                facility_details: facilityDetails,
              }) ?? {
                id: facility.id,
                facility_name: facility.facility_name || facilityDetails?.facility_name || "",
                facility_type: facility.facility_type || facilityDetails?.facility_type || "",
                facility_type_id: facilityTypeId,
                facility_details: facilityDetails,
              };
              
              sessionStorage.setItem(
                "preferred_facility_for_pricing",
                JSON.stringify(facilityToStore)
              );
              
              console.log("💾 Stored facility in sessionStorage:", facilityToStore);
              
              // If promo code is not in URL but facility has one, use it
              if (!promoCodeParam && facility.discount_coupon_name) {
                setPromoCodeFromUrl(facility.discount_coupon_name);
              }
              // Mark facility as loaded to trigger pricing tiers reload
              setFacilityLoadedFromUrl(true);
            }
          } catch (error) {
            console.error("❌ Failed to fetch facility details:", error);
            setFacilityLoadedFromUrl(false);
          }
        };
        fetchFacilityDetails();
      }
    } else {
      setFacilityIdFromUrl(null);
      setFacilityLoadedFromUrl(false);
    }

    if (promoCodeParam) {
      setPromoCodeFromUrl(promoCodeParam);
    } else if (!facilityIdParam) {
      // Only clear promo code if there's no user_preferred_facility_id (to allow facility's promo code to be used)
      setPromoCodeFromUrl(null);
    }
  }, [location.search]);

  // When user lands with facility + promo from URL, pre-select lowest-cost paid plan (wait for user to click Pay now).
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    // Lowest-cost pre-select is only the landing default. An explicit
    // selected_plan_id (e.g. order-summary) is the user's choice and wins.
    if (!facilityLoadedFromUrl || !pricingTiers.length || searchParams.get("selected_plan_id")) return;
    try {
      const promoFromUrl = searchParams.get("promo_code");
      const facility = readPreferredFacilityForPricing<{
        discount_coupon_name?: string | null;
      }>(routePreferredFacilityId);
      const promoFromFacility = facility?.discount_coupon_name?.trim();
      const hasPromo = !!(promoFromUrl?.trim() || promoFromFacility);
      if (!hasPromo) return;
      const paidTiers = pricingTiers.filter(
        (t) => !(t as any).is_free && (t as any).redirect_to !== "contactus"
      );
      if (!paidTiers.length) return;
      const withPrice = paidTiers.map((t) => {
        const raw = (t as any).price as string | number | undefined;
        const num = typeof raw === "number" ? raw : parseFloat(String(raw ?? "0"));
        return { tier: t, price: Number.isNaN(num) ? 0 : num };
      });
      withPrice.sort((a, b) => a.price - b.price);
      const lowestPlanId = String(withPrice[0].tier.id);
      setSelectedPlanId(lowestPlanId);
    } catch (_) {
      // ignore
    }
  }, [facilityLoadedFromUrl, pricingTiers, location.search, routePreferredFacilityId]);


  React.useEffect(() => {
    let isCancelled = false;
    // Clear old value immediately when billing toggle/plan changes to avoid stale flicker.
    setAnnualSavePercent(null);

    const loadPlanComparisonPercent = async () => {
      if (!selectedPlanId) {
        setAnnualSavePercent(null);
        return;
      }
      const selectedTier = pricingTiers.find(
        (tier) => String((tier as any).id) === String(selectedPlanId),
      );
      const planId = Number(selectedTier?.id ?? NaN);
      if (!planId || Number.isNaN(planId)) {
        setAnnualSavePercent(null);
        return;
      }

      try {
        let verticalForComparison = "healthcare";
        try {
          const parsedPreferred = readPreferredFacilityForPricing(routePreferredFacilityId);
          if (parsedPreferred) {
            const details = parsedPreferred?.facility_details ?? {};
            const resolvedVertical =
              parsedPreferred?.vertical ??
              details?.vertical ??
              parsedPreferred?.facility?.vertical;
            if (
              typeof resolvedVertical === "string" &&
              resolvedVertical.trim() !== ""
            ) {
              verticalForComparison = resolvedVertical.trim();
            }
          }
        } catch (_) {
          // ignore
        }
        if (filtered_selectedFacility?.vertical) {
          verticalForComparison = String(filtered_selectedFacility.vertical);
        }

        const response = await getAnnualPlanComparison(
          planId,
          isMonthly ? "monthly" : "annual",
          {
            sku_name: "SKU.Unicomply.Rules",
            vertical: verticalForComparison,
          },
        );
        const payload =
          (response as any)?.data ??
          (response as any)?.result?.data ??
          (response as any)?.result ??
          {};
        const percent = Number(
            payload.difference_percentage
        );
        if (!isCancelled) {
          setAnnualSavePercent(Number.isFinite(percent) && percent > 0 ? percent : null);
        }
      } catch {
        if (!isCancelled) setAnnualSavePercent(null);
      }
    };

    loadPlanComparisonPercent();
    return () => {
      isCancelled = true;
    };
  }, [isMonthly, selectedPlanId, pricingTiers, selectedFacilityVertical, routePreferredFacilityId]);

  // After billing cycle toggle, auto-select the plan with the same name in the new cycle
  React.useEffect(() => {
    if (loading || !pricingTiers.length || !pendingMatchNameRef.current) return;
    const matchName = pendingMatchNameRef.current.toLowerCase();
    const matched = pricingTiers.find((t) => {
      const name = ((t as any).display_name ?? (t as any).name ?? (t as any).tier_name ?? "").toLowerCase();
      return name === matchName;
    });
    if (matched) {
      setSelectedPlanId(String((matched as any).id));
    }
    pendingMatchNameRef.current = null;
  }, [pricingTiers, loading]);

  // Guard: redirect if not authenticated OR if accessed via /Pricing route (should use /dashboard/facilities)
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const userPreferredFacilityId = searchParams.get("user_preferred_facility_id");
    const promoCode = searchParams.get("promo_code");
    const isOnStandalonePricingRoute = location.pathname === "/Pricing";
    const isAuthenticated = isLocalAuthenticated();
    
    // If not authenticated, redirect to login with params preserved
    if (!isAuthenticated) {
      if (userPreferredFacilityId) {
        const loginParams = new URLSearchParams();
        loginParams.set("user_preferred_facility_id", userPreferredFacilityId);
        if (promoCode) {
          loginParams.set("promo_code", promoCode);
        }
        navigate(`/login?${loginParams.toString()}`, { replace: true });
      } else {
        navigate("/login", { replace: true });
      }
      return;
    }
    
    // If authenticated and accessed via /Pricing route, redirect to /dashboard/facilities to show full UI
    if (isOnStandalonePricingRoute && !isNoOrgAccountSession()) {
      const dashboardParams = new URLSearchParams();
      if (userPreferredFacilityId) {
        dashboardParams.set("user_preferred_facility_id", userPreferredFacilityId);
      }
      if (promoCode) {
        dashboardParams.set("promo_code", promoCode);
      }
      const queryString = dashboardParams.toString();
      navigate(`/dashboard/facilities${queryString ? `?${queryString}` : ""}`, {
        state: { currentView: "pricing" },
        replace: true,
      });
      return;
    }
  }, [navigate, location.search, location.pathname]);

  const getCurrentUserId = (): string => {
    try {
      const userRaw = localStorage.getItem("user");
      if (userRaw) {
        const user = JSON.parse(userRaw);
        return String(user?.id ?? user?.user_id ?? "0");
      }
    } catch (_) {}
    return "0";
  };
  /** Username prefix for placeholder NPI (username_identifier format). Prefer actual username so backend/audit can tie placeholder to user; fallback to FAKE. */
  const getPlaceholderNpiPrefix = (): string => {
    try {
      const userRaw = localStorage.getItem("user");
      if (userRaw) {
        const user = JSON.parse(userRaw) as { username?: string; email?: string; [k: string]: unknown };
        const fromUsername = typeof user?.username === "string" && user.username.trim() ? user.username.trim() : null;
        const fromEmail = typeof user?.email === "string" && user.email.includes("@") ? user.email.split("@")[0] : null;
        const raw = fromUsername || fromEmail || "";
        const sanitized = raw.replace(/\./g, "").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase().slice(0, 32) || "";
        if (sanitized) return sanitized;
      }
    } catch (_) {}
    return "FAKE";
  };

  const generatePlaceholderNPI = (): string => {
    // username_identifier format: prefix_<digits> (backend accepts e.g. username_1234567890)
    const timestamp = Date.now().toString().slice(-10);
    return getPlaceholderNpiPrefix() + "_" + timestamp;
  };
  const handleContinue = async (appliedCode?: string, options?: { skipToDashboard?: boolean }) => {
    if (!selectedPlanId) return;
    const plan = pricingTiers.find((p) => p.id.toString() === selectedPlanId);
    if (!plan) return;

    const userId = getCurrentUserId();

    // If free plan that should redirect to Contact Us, just navigate there
    if (plan.is_free && (plan as any).redirect_to === "contactus") {
      navigate("/dashboard/contact-us", {
        state: { fromPricingContactUsFlow: true },
      });
      return;
    }

    if (plan.is_free) {
      // Call user preferred plan API for free plans
      try {
        const userPreferredPlanPayload = {
          user_id: parseInt(userId),
          plan_tier_id: plan.id,
        };
        await createUserPreferredPlan(userPreferredPlanPayload);
        try {
          sessionStorage.setItem(
            "selected_plan",
            JSON.stringify({ id: plan.id, title: plan.display_name })
          );
        } catch (_) {}
      } catch (error) {
        console.error("Error creating user preferred plan:", error);
      }
      navigate("/dashboard/facilities", {
        state: { currentView: "spaces" },
      });
      return;
    }

    // Unified checkout flow for paid and bypass codes
    try {
      setIsCreatingCheckout(true);
      // Evaluate discount code (if any)
      let isBypass = getCurrentUser()?.can_sales_bypass === true;

      // Load preferred facility (from user-preferred-facilities API response) if present.
      // We may need its discount_coupon_name for 100% discount logic.
      let preferredFacilityForPricing: any = null;
      try {
        preferredFacilityForPricing = readPreferredFacilityForPricing(routePreferredFacilityId);
      } catch (_) {
        preferredFacilityForPricing = null;
      }

      const leftoverStored = readLeftoverUnpaidIdentity();
      let leftoverForPromo =
        preferredFacilityForPricing &&
        leftoverStored &&
        !leftoverFacilitiesMatch(leftoverStored, preferredFacilityForPricing)
          ? pickLeftoverUnpaidSource(preferredFacilityForPricing)
          : pickLeftoverUnpaidSource(
              leftoverStored,
              preferredFacilityForPricing,
              filtered_selectedFacility,
            );

      // Access Restricted → Update Payment: only user-applied Order Summary code.
      // Skip leftover/admin FREE_30 rehydrate (that was silent Stripe bypass).
      let facilityDiscountCode: string | null = null;
      let resolvedPromo: string | null =
        String(appliedCode ?? "").trim() || null;

      if (!fromRestrictedUpdatePayment) {
        if (leftoverForPromo) {
          const merged = await fetchAndMergeAdminPromoForLeftover(leftoverForPromo);
          if (merged) {
            persistLeftoverUnpaidIdentity(merged);
            persistPreferredFacilityForSelectPlan(merged);
            setFilteredSelectedFacility(readFilteredSelectedFacility());
            preferredFacilityForPricing = merged;
            leftoverForPromo = merged;
          }
        }
        facilityDiscountCode =
          preferredFacilityForPricing?.discount_coupon_name ||
          preferredFacilityForPricing?.facility_details?.discount_coupon_name ||
          leftoverForPromo?.discount_coupon_name ||
          null;
        // AB#10916: leftover Mackay promo on admin row even if Pay Now omitted appliedCode.
        resolvedPromo = resolveLeftoverPayNowPromo({
          appliedPromo: appliedCode,
          facilityDiscountCode,
        });
      }

      const leftoverPayNowNpi = resolveLeftoverPayNowNpi(
        preferredFacilityForPricing,
        leftoverForPromo,
        filtered_selectedFacility,
        leftoverStored,
      );

      if (resolvedPromo) {
        const promo = resolvedPromo;
        // Dynamic year-month code is sales-only. Regular customers typing
        // "15" in Nov 2026 must not get a free checkout.
        if (isSalesOnlyDynamicBypassCode(promo, isBypass)) {
          isBypass = true;
        }

        // 1) Check discount codes from API when we have a promo and haven't already set bypass
        if (!isBypass) {
          try {
            const { codes } = await getDiscountCodes();
            const found = codes.find(
              (c) => c.name.toUpperCase() === promo.toUpperCase()
            );
            if (found && found.type === "bypass") {
              isBypass = true;
            }
          } catch (_) {}
        }

        // 2) Facility-specific discount: if promo matches the
        // discount_coupon_name assigned to this facility, grant 100% discount
        // and treat this as bypass (no Stripe payment).
        if (
          facilityDiscountCode &&
          promo.toUpperCase() === facilityDiscountCode.toUpperCase()
        ) {
          isBypass = true;
        }
      }

      // Save plan selection in session
      try {
        sessionStorage.setItem("selected_plan", JSON.stringify({ id: plan.id, title: plan.display_name }));
      } catch (_) {}

      const currentDomain = window.location.origin;

      const preferredDetails = preferredFacilityForPricing?.facility_details;

      const trimmedNpiNumber = npiNumber?.trim();
      // Association unique id: Rules-safe Medicare CCN when present, else leftover NPI.
      // CI 33040/33074: bare short CCNs 422 Rules; username_* CCN ids still preferred
      // over NPI so Mackay does not twin-mint when a Rules-safe CCN unique id exists.
      const npiNumberValue =
        associationUniqueIdFromLookupFacility(
          {
            ccn_number:
              preferredDetails?.ccn_number ||
              preferredFacilityForPricing?.ccn_number ||
              filtered_selectedFacility?.ccn_number ||
              filtered_selectedFacility?.ccn,
            ccn: filtered_selectedFacility?.ccn,
            npi_number:
              preferredDetails?.npi_number ||
              preferredFacilityForPricing?.npi_number ||
              leftoverPayNowNpi ||
              trimmedNpiNumber,
            npi: filtered_selectedFacility?.npi_number,
            facility_unique_id:
              preferredDetails?.facility_unique_id ||
              preferredFacilityForPricing?.facility_unique_id,
          },
          leftoverPayNowNpi.length === 10
            ? leftoverPayNowNpi
            : trimmedNpiNumber && trimmedNpiNumber.length === 10 && /^\d+$/.test(trimmedNpiNumber)
              ? trimmedNpiNumber
              : "",
        ) || generatePlaceholderNPI();
      
      // Check user-facility association before creating checkout session
      if (filtered_selectedFacility?.site_name) {
        try {
          const associationPayload = {
            ccn_number: npiNumberValue,
            facility_name: filtered_selectedFacility.site_name,
            city: filtered_selectedFacility.city || "",
            state: filtered_selectedFacility.state || "",
            country: getSelectedCountryCode(),
            facility_type: filtered_selectedFacility.facility_type || "",
          };
          
          await checkUserFacilityAssociation(associationPayload);
          console.log("✅ User facility association checked successfully");
        } catch (error) {
          console.error("❌ Failed to check user facility association:", error);
          // Continue with checkout even if association check fails
          // You can add error handling here if needed
        }
      }

      // Prefer leftover / preferred root (has leftover NPI) over details-only blob.
      const facilityMetaSource: any =
        preferredFacilityForPricing ||
        leftoverForPromo ||
        preferredDetails ||
        filtered_selectedFacility ||
        null;
      const identityDetails =
        facilityMetaSource?.facility_details || preferredDetails || {};
      const fallbackFacility = filtered_selectedFacility || {};
      const fallbackDetails = fallbackFacility.facility_details || {};
      const facilityTargeting =
        facilityMetaSource?.profile_targeting_json ??
        preferredFacilityForPricing?.profile_targeting_json ??
        null;
      const primaryProfileSlug =
        facilityMetaSource?.primary_profile_slug ??
        preferredFacilityForPricing?.primary_profile_slug ??
        facilityTargeting?.primary_profile_slug ??
        null;
      const checkoutFacilityType = resolveCheckoutFacilityType(
        preferredFacilityForPricing,
        leftoverForPromo,
        leftoverStored,
        filtered_selectedFacility,
      );
      const checkoutFacilityTypeId = resolveCheckoutFacilityTypeId(
        preferredFacilityForPricing,
        leftoverForPromo,
        leftoverStored,
        filtered_selectedFacility,
      );
      const checkoutTaxonomy = resolveCheckoutTaxonomy(
        preferredFacilityForPricing,
        leftoverForPromo,
        leftoverStored,
        filtered_selectedFacility,
      );

      const leftoverPreferredFacilityId =
        resolveCheckoutPreferredFacilityPk(preferredFacilityForPricing) ??
        leftoverPreferredPk(preferredFacilityForPricing) ??
        leftoverPreferredPk(leftoverForPromo);
      const selfReported = isSelfReportedPreferredFacility(facilityMetaSource);
      const selfReportedSlug =
        selfReportedFacilityTypeSlug(facilityMetaSource) ||
        selfReportedFacilityTypeSlug(preferredFacilityForPricing);
      const selfReportedLicense =
        selfReportedLicenseNumber(facilityMetaSource) ||
        selfReportedLicenseNumber(preferredFacilityForPricing);
      const selfReportedUnique = selfReported
        ? selfReportedUniqueId(leftoverPreferredFacilityId, selfReportedLicense)
        : "";

      const payload = {
        metadata: {
          user_id: userId,
          ccn_number: checkoutMetadataValue(
            leftoverPayNowNpi.length === 10
              ? leftoverPayNowNpi
              : leftoverFacilityNpi(facilityMetaSource) ||
                facilityMetaSource?.ccn_number ||
                (selfReported && selfReportedUnique
                  ? selfReportedUnique
                  : npiNumberValue),
          ),
          facility_name: checkoutMetadataValue(
            facilityMetaSource?.facility_name ||
              facilityMetaSource?.site_name ||
              identityDetails.facility_name ||
              identityDetails.site_name ||
              fallbackFacility.facility_name ||
              fallbackFacility.site_name ||
              fallbackDetails.facility_name,
          ),
          facility_type: checkoutMetadataValue(checkoutFacilityType),
          facility_type_id: checkoutFacilityTypeId ?? undefined,
          ...(selfReportedSlug
            ? { facility_type_slug: selfReportedSlug }
            : {}),
          ...(selfReported
            ? { verification_source: SELF_REPORTED_VERIFICATION_SOURCE }
            : {}),
          ...(selfReportedLicense
            ? { state_license_number: selfReportedLicense }
            : {}),
          ...(checkoutTaxonomy.code && {
            primary_taxonomy_code: checkoutTaxonomy.code,
          }),
          ...(checkoutTaxonomy.desc && {
            primary_taxonomy_desc: checkoutTaxonomy.desc,
          }),
          vertical: checkoutMetadataValue(
            facilityMetaSource?.vertical ??
              preferredFacilityForPricing?.vertical,
          ),
          primary_profile_slug: primaryProfileSlug ?? undefined,
          profile_targeting_json: facilityTargeting ?? undefined,
          city: checkoutMetadataValue(
            facilityMetaSource?.city ||
              identityDetails.city ||
              fallbackFacility.city ||
              fallbackDetails.city,
          ),
          state: checkoutMetadataValue(
            facilityMetaSource?.state ||
              identityDetails.state ||
              fallbackFacility.state ||
              fallbackDetails.state,
          ),
          country: checkoutMetadataValue(
            facilityMetaSource?.country ?? getSelectedCountryCode() ?? "US",
          ),
          address_1: checkoutMetadataValue(
            facilityMetaSource?.address_1 ??
              facilityMetaSource?.primary_address?.address_1 ??
              preferredDetails?.address_1,
          ),
          address_2: checkoutMetadataValue(
            facilityMetaSource?.address_2 ??
              facilityMetaSource?.primary_address?.address_2 ??
              preferredDetails?.address_2,
          ),
          postal_code: checkoutMetadataValue(
            facilityMetaSource?.postal_code ??
              facilityMetaSource?.primary_address?.postal_code ??
              preferredDetails?.postal_code ??
              preferredDetails?.primary_address?.postal_code,
          ),
          phone_number: checkoutMetadataValue(
            typeof facilityMetaSource?.phone_number !== "undefined"
              ? facilityMetaSource.phone_number
              : phoneNumber || undefined,
          ),
          skip_npi_validation:
            typeof facilityMetaSource?.skip_npi_validation === "boolean"
              ? facilityMetaSource.skip_npi_validation
              : selfReported
              ? true
              : Boolean(trimmedNpiNumber)
              ? false
              : true,
          skip_ccn_validation:
            selfReported ||
            facilityMetaSource?.skip_npi_validation === true ||
            facilityMetaSource?.skip_ccn_validation === true,
          username: userName,
          user_type: userPlan,
          pricing_tier_id: plan.id,
          bypass_checkout: isBypass,
          ...(resolvedPromo && { discount_coupon: resolvedPromo }),
          ...(leftoverPreferredFacilityId != null
            ? { user_preferred_facility_id: leftoverPreferredFacilityId }
            : {}),
        },
        pricing_tier_id: plan.id,
        success_url: `${currentDomain}/payment-success`,
        cancel_url: `${currentDomain}/payment-failure`,
      } as const;
      const postPurchaseFacilityState = buildPostPurchaseFacilityState(
        preferredFacilityForPricing,
        facilityMetaSource,
      );
      try {
        sessionStorage.setItem(
          POST_PURCHASE_FACILITY_SELECTION_KEY,
          JSON.stringify(postPurchaseFacilityState),
        );
      } catch (_) {}

      const data = await createCheckoutSession(payload);
      const checkoutUrl = data?.result?.checkout_url;
      const isSuccess = data?.success ?? false;
      // Backend may auto-bypass when a valid bypass discount code is applied (e.g. MISSIFREE), returning success with no checkout_url
      const backendBypass = isSuccess && !checkoutUrl;

      const rawFid: unknown = data?.result?.facility_id;
      const checkoutRulesFacilityId =
        typeof rawFid === "number" && Number.isFinite(rawFid)
          ? rawFid
          : typeof rawFid === "string" &&
              rawFid.trim() !== "" &&
              Number.isFinite(Number(rawFid))
            ? Number(rawFid)
            : undefined;
      const postPurchaseWithServerFacility =
        checkoutRulesFacilityId != null
          ? buildPostPurchaseFacilityState(
              preferredFacilityForPricing,
              facilityMetaSource,
              checkoutRulesFacilityId,
            )
          : postPurchaseFacilityState;
      if (checkoutRulesFacilityId != null) {
        try {
          sessionStorage.setItem(
            POST_PURCHASE_FACILITY_SELECTION_KEY,
            JSON.stringify(postPurchaseWithServerFacility),
          );
        } catch (_) {}
      }

      if (isSuccess) {
        if (
          shouldClearAccessRestrictedAfterPromoPayNow({
            appliedPromo: appliedCode,
            facilityDiscountCode,
            checkoutSuccess: true,
          })
        ) {
          const markSource =
            preferredFacilityForPricing ||
            leftoverForPromo ||
            facilityMetaSource ||
            filtered_selectedFacility;
          markPromoCheckoutClearedRestricted({
            ...markSource,
            ccn_number:
              leftoverPayNowNpi ||
              leftoverFacilityNpi(markSource) ||
              leftoverFacilityNpi(preferredFacilityForPricing) ||
              leftoverFacilityNpi(filtered_selectedFacility) ||
              markSource?.ccn_number,
            npi_number: leftoverPayNowNpi || leftoverFacilityNpi(markSource),
            facility_id:
              leftoverFacilityId(markSource) ??
              leftoverFacilityId(preferredFacilityForPricing) ??
              leftoverFacilityId(filtered_selectedFacility),
          });
        }
        invalidateMyFacilitiesCache();
        try {
          sessionStorage.removeItem("filtered_selectedFacility");
        } catch (_) {}
        if (checkoutRulesFacilityId != null && postPurchaseWithServerFacility?.facilityName) {
          prewarmPostCheckoutCompliance({
            facilityId: checkoutRulesFacilityId,
            facilityName: postPurchaseWithServerFacility.facilityName,
            city: postPurchaseWithServerFacility.facilityCity,
            state:
              facilityMetaSource?.state ??
              preferredFacilityForPricing?.facility_details?.state ??
              null,
            facilityTypeSlug: primaryProfileSlug ?? undefined,
            primaryProfileSlug: primaryProfileSlug ?? undefined,
            vertical:
              facilityMetaSource?.vertical ??
              preferredFacilityForPricing?.vertical ??
              undefined,
          });
        }
        if (checkoutUrl && !isBypass && !backendBypass) {
          window.location.href = checkoutUrl;
        } else if (isBypass || backendBypass) {
          // For bypass or backend auto-bypass, go directly to My Facility.
          // Sync preview vertical first so getMyFacilities loads aviation/etc.
          // instead of defaulting to healthcare until the user opens the switcher.
          const purchasedVertical =
            postPurchaseWithServerFacility.vertical ??
            facilityMetaSource?.vertical ??
            preferredFacilityForPricing?.vertical ??
            preferredFacilityForPricing?.facility_details?.vertical;
          syncPreviewVerticalAfterPurchase(purchasedVertical);
          // Path `/MyFacility` selects Active Facility even if router state is dropped;
          // `/facilities` alone defaults to Regulations Library (spaces).
          navigate("/dashboard/MyFacility", {
            state: {
              ...postPurchaseWithServerFacility,
              refreshFacilities: true,
            },
          });
          return;
        } else {
          sessionStorage.removeItem("selected_plan");
          const purchasedVertical =
            postPurchaseWithServerFacility.vertical ??
            facilityMetaSource?.vertical ??
            preferredFacilityForPricing?.vertical ??
            preferredFacilityForPricing?.facility_details?.vertical;
          syncPreviewVerticalAfterPurchase(purchasedVertical);
          navigate("/dashboard/MyFacility", {
            state: {
              ...postPurchaseWithServerFacility,
              refreshFacilities: true,
            },
          });
        }
      } else {
        // Show toast when success is false
        toast.error("Unable to process payment. Please try again. If issue persists, contact us at support@affableone.ai");
        sessionStorage.removeItem("selected_plan");
      }
    } catch (e: any) {
      sessionStorage.removeItem("selected_plan");
      console.error("Checkout session error:", e);
      toast.error("Unable to process payment. Please try again. If issue persists, contact us at support@affableone.ai");
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  // Convert API data to component format
  const pricingPlans = pricingTiers
    .filter((tier) => (tier as any).is_active === 1)
    .sort((a, b) => ((a as any).display_order ?? 0) - ((b as any).display_order ?? 0))
    .map((tier) => ({
      id: tier.id.toString(),
      title: (tier as any).display_name ?? (tier as any).name ?? (tier as any).tier_name ?? "",
      price:
        (tier as any).is_free && (tier as any).redirect_to === "contactus"
          ? "Contact Us"
          : (tier as any).is_free
          ? "Free"
          : (() => {
              const raw = (tier as any).price as string | number | undefined;
              const num = typeof raw === "number" ? raw : parseFloat(String(raw ?? ""));
              const displayNum =
                Number.isNaN(num)
                  ? NaN
                  : isMonthly
                  ? num
                  : num / 12;
              const normalized = Number.isNaN(displayNum)
                ? String(raw ?? "")
                : displayNum % 1 === 0
                ? String(Math.trunc(displayNum))
                : String(displayNum.toFixed(2));
              const symbol = (tier as any).region_currency_symbol || (tier as any).currency || "";
              return symbol ? `${symbol}${normalized}` : normalized;
            })(),
      period: (tier as any).is_free ? undefined : ((tier as any).period ?? (tier as any).frequency_name ?? undefined),
      description: (tier as any).description ?? "",
      features:
        ((tier as any).plan_details?.[0]?.plan_data as string[] | undefined) ??
        ((tier as any).formatted_features as string[] | undefined) ??
        ((((tier as any).display_features?.map((f: any) => f.name)) as string[] | undefined) ?? []),
      isPopular: (tier as any).is_popular,
      buttonText: `Get ${((tier as any).display_name ?? (tier as any).name ?? (tier as any).tier_name ?? "Selected")} Plan`,
      buttonVariant: ((tier as any).is_free ? "primary" : "secondary") as
        | "primary"
        | "secondary",
      isFree: (tier as any).is_free,
      specialFeatureIndex: undefined,
    }));

  return (
    <PricingSection id="pricing">
      {/* Full-page overlay while checkout is being created */}
      {isCreatingCheckout && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={56} sx={{ color: "#0a829d" }} />
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 500,
              color: "#065b70",
            }}
          >
            Processing your payment data, please wait...
          </Typography>
        </Box>
      )}

      <Container
        maxWidth={false}
        sx={{
          maxWidth: "1250px",
          margin: "0 auto",
          paddingLeft: "50px !important",
          paddingRight: "50px !important",
        }}
      >
        {!isOrderSummaryPage && (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 2,
                marginBottom: 3,
                borderBottom:
                  "1px solid var(--Brand-Colors-color--grad-1, #35D7E4)",
                background: "rgba(253, 255, 255, 0.20)",
              }}
            >
              {/* Title Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {fromFacilityValidation && (
                  <IconButton
                    type="button"
                    aria-label="Back to facility validation"
                    onClick={handleBackToFacilityValidation}
                    sx={{
                      color: "#065b70",
                      padding: "4px",
                      "&:hover": { backgroundColor: "rgba(10, 130, 157, 0.08)" },
                    }}
                  >
                    <ArrowBackIcon size={28} color="#065b70" />
                  </IconButton>
                )}
                {/* Title */}
                <Typography
                  sx={{
                    color: "var(--Text-and-Icon-Primary, #262626)",
                    fontFamily: '"Rethink Sans"',
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "150%",
                    letterSpacing: "-1px",
                  }}
                >
                  Select Plan
                </Typography>

                {/* Badge */}
                {/* <Box
                  sx={{
                    display: "flex",
                    padding: "4px 12px",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                    borderRadius: "16px",
                    border: "1px solid var(--Primary-Color-Palette-300, #99D4E1)",
                    background: "#EAFDFF",
                  }}
                >
                  <Typography
                    sx={{
                      color: "var(--Primary-Color-Palette-800, #05495A)",
                      fontFamily: '"Rethink Sans"',
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "150%",
                    }}
                  >
                    {userPlan}
                  </Typography>
                </Box> */}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 2,
                marginBottom: 3,
              }}
            >
              {/* Title */}
              <Typography
                component="div"
                sx={{
                  color: "var(--Brand-Colors-color-brand-Pressed, #065B70)",
                  fontFamily: '"Rethink Sans"',
                  fontSize: "20px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "normal",
                }}
              >
                <Box component="span">You have selected</Box>
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  {" "}
                  {(() => {
                    // Prefer preferred_facility_for_pricing (from API / user-preferred-facility_id) so display matches API
                    try {
                      const parsed = readPreferredFacilityForPricing(routePreferredFacilityId);
                      if (parsed) {
                        const name = parsed.facility_name || parsed.site_name || "";
                        if (name) return name;
                      }
                    } catch (err) {
                      console.error("Error parsing preferred facility:", err);
                    }
                    if (filtered_selectedFacility?.site_name) {
                      return filtered_selectedFacility.site_name;
                    }
                    return "";
                  })()}
                </Box>
                <Box component="span"> an </Box>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {(() => {
                    // Prefer preferred_facility_for_pricing (from API) so facility type matches user-preferred-facilities response
                    try {
                      const parsed = readPreferredFacilityForPricing(routePreferredFacilityId);
                      if (parsed) {
                        const leftover = readLeftoverUnpaidIdentity();
                        const type =
                          resolveCheckoutFacilityType(parsed, leftover) ||
                          parsed.facility_type ||
                          parsed.facility_details?.facility_type ||
                          "";
                        if (type) return type;
                      }
                    } catch (err) {
                      console.error("Error parsing preferred facility:", err);
                    }
                    if (filtered_selectedFacility?.facility_type) {
                      return filtered_selectedFacility.facility_type;
                    }
                    return "";
                  })()}
                </Box>
                <Box component="div" sx={{ mt: 1 }}>
                  This facility will be attached to
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {" "}
                    {userEmail}
                  </Box>
                </Box>
              </Typography>
            </Box>
          </>
        )}
        {!isOrderSummaryPage && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "15px",
              width: "100%",
              paddingBottom: "15px",
            }}
          >
            {/* Annually | Switch | Monthly */}
            <BillingToggle>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Box
                  onClick={() => {
                    if (lastSelectedPlanNameRef.current) {
                      pendingMatchNameRef.current = lastSelectedPlanNameRef.current;
                    }
                    setIsMonthly(false);
                    setSelectedPlanId(null);
                  }}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    px: "12px",
                    py: "7px",
                    borderRadius: "16px",
                    cursor: "pointer",
                    background: isMonthly
                      ? "transparent"
                      : "linear-gradient(94deg, #2FCFDE -2.59%, #0A829D 115.51%)",
                  }}
                >
                  <Typography
                    sx={{
                      color: isMonthly ? "#2D4A53" : "#FFFFFF",
                      fontSize: "14px",
                      fontWeight: isMonthly ? 500 : 700,
                      lineHeight: "20px",
                    }}
                  >
                    Annually
                  </Typography>
                  {!loading &&
                    selectedPlanId !== null &&
                    annualSavePercent !== null &&
                    annualSavePercent > 0 && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: "8px",
                        py: "3px",
                        borderRadius: "12px",
                        fontFamily: "Rethink Sans, sans-serif",
                        fontSize: "12px",
                        lineHeight: "16px",
                        fontWeight: 700,
                        color: "#065B70",
                        background: "#BFEAF3",
                        border: "1px solid #90D4E3",
                      }}
                    >
                      {`Save ${annualSavePercent}%`}
                    </Box>
                  )}
                </Box>

                <Typography
                  onClick={() => {
                    if (lastSelectedPlanNameRef.current) {
                      pendingMatchNameRef.current = lastSelectedPlanNameRef.current;
                    }
                    setIsMonthly(true);
                    setSelectedPlanId(null);
                  }}
                  sx={{
                    color: isMonthly ? "#FFFFFF" : "#2D4A53",
                    fontSize: "14px",
                    fontWeight: isMonthly ? 700 : 500,
                    lineHeight: "20px",
                    cursor: "pointer",
                    px: "12px",
                    py: "7px",
                    borderRadius: "16px",
                    background: isMonthly
                      ? "linear-gradient(94deg, #2FCFDE -2.59%, #0A829D 115.51%)"
                      : "transparent",
                  }}
                >
                  Monthly
                </Typography>
              </Box>
            </BillingToggle>
          </Box>
        )}

        {/* Pricing Cards or Loader within content area */}
        {loading ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={48} sx={{ color: "#0a829d" }} />
              <Typography
                sx={{ color: "#737373", fontSize: 16, fontWeight: 500 }}
              >
                Loading pricing plans...
              </Typography>
            </Box>
          </Box>
        ) : isOrderSummaryPage ? null : (
          <Grid
            container
            spacing={pricingPlans.length === 3 ? 2.5 : 1}
            justifyContent="center"
            alignItems="stretch"
            sx={{ paddingTop: "3px", maxWidth: "1120px", margin: "0 auto" }}
          >
            {pricingPlans.map((plan, index) => (
              <Grid
                item
                xs={12}
                sm={pricingPlans.length === 3 ? 4 : 6}
                md={pricingPlans.length === 3 ? 4 : 3}
                lg={pricingPlans.length === 3 ? 4 : 3}
                key={index}
              >
                <PricingCardComponent
                  title={plan.title}
                  price={plan.price}
                  period={plan.period}
                  description={plan.description}
                  features={plan.features}
                  isPopular={plan.isPopular}
                  buttonText={plan.buttonText}
                  buttonVariant={plan.buttonVariant}
                  specialFeatureIndex={plan.specialFeatureIndex}
                  id={plan.id}
                  onSelect={(id) => {
                    const chosenId = String(id);
                    setSelectedPlanId(chosenId);
                    const selectedTier = pricingTiers.find(
                      (tier) => String((tier as any).id) === chosenId
                    );
                    const shouldRedirectToContactUs =
                      (selectedTier as any)?.redirect_to === "contactus";

                    if (shouldRedirectToContactUs) {
                      navigate("/dashboard/contact-us", {
                        state: { fromPricingContactUsFlow: true },
                      });
                      return;
                    }

                    if (!(plan as any).is_free) {
                      const params = new URLSearchParams(location.search);
                      params.set("selected_plan_id", chosenId);
                      navigate(`/dashboard/pricing/order-summary?${params.toString()}`, { state: { isMonthly } });
                    }
                  }}
                  onCardSelect={(id) => {
                    const idStr = String(id);
                    const tier = pricingTiers.find((t) => String((t as any).id) === idStr);
                    const name = (tier as any)?.display_name ?? (tier as any)?.name ?? (tier as any)?.tier_name ?? null;
                    if (name) lastSelectedPlanNameRef.current = name;
                    setSelectedPlanId(idStr);
                  }}
                  onHoverChange={(id) => setHoveredPlanId(id)}
                  isHovered={hoveredPlanId === String(plan.id)}
                  isSelected={selectedPlanId === String(plan.id)}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Order Summary below plans */}
        {(() => {
          if (!isOrderSummaryPage) return null;
          if (!selectedPlanId) return null;
          const selectedPlan = pricingTiers.find((p) => p.id.toString() === selectedPlanId);
          if (!selectedPlan || selectedPlan.is_free) return null;

          const priceValue = (() => {
            const raw = (selectedPlan as any).price as string | number | undefined;
            const num = typeof raw === "number" ? raw : parseFloat(String(raw ?? "0"));
            return Number.isNaN(num) ? 0 : num;
          })();

          return (
            <Box sx={{ mt: 4 }}>
              <Grid container justifyContent="center">
                <Grid item xs={12} md={8} lg={6}>
                  <OrderSummary
                    planName={(selectedPlan as any).display_name || (selectedPlan as any).name || (selectedPlan as any).tier_name || ""}
                    planPrice={priceValue}
                    currency={(selectedPlan as any).region_currency_symbol || (selectedPlan as any).currency || ""}
                    period={(selectedPlan as any).period || (selectedPlan as any).frequency_name || "Per Facility per month"}
                    onCancel={() => {
                      const params = new URLSearchParams(location.search);
                      params.delete("selected_plan_id");
                      const query = params.toString();
                      navigate(`/dashboard/pricing${query ? `?${query}` : ""}`);
                    }}
                    onPayNow={handleContinue}
                    isProcessing={isCreatingCheckout}
                    initialDiscountCode={(() => {
                      if (fromRestrictedUpdatePayment) return undefined;
                      if (promoCodeFromUrl) return promoCodeFromUrl;
                      try {
                        const parsed = readPreferredFacilityForPricing(routePreferredFacilityId);
                        if (!parsed) return undefined;
                        const d = parsed?.discount_coupon_name;
                        return (d && typeof d === "string" && d.trim()) ? d.trim() : undefined;
                      } catch { return undefined; }
                    })()}
                    // Debug: Log when passing promo code to OrderSummary
                    // Note: This will cause a re-render, but helps debug
                    onApplyDiscount={async (code) => {
                      try {
                        // First, check facility-specific discount from preferred_facility_for_pricing
                        let facilityDiscountCode: string | null = null;
                        let discountEndDate: string | null = null;
                        try {
                          const parsed = readPreferredFacilityForPricing(routePreferredFacilityId);
                          if (parsed) {
                            console.log("parsed--------------", parsed);
                            facilityDiscountCode =
                              parsed?.discount_coupon_name || null;
                            discountEndDate = parsed?.discount_end_date || null;
                          }
                        } catch (_) {
                          facilityDiscountCode = null;
                          discountEndDate = null;
                        }

                        if (
                          facilityDiscountCode &&
                          code.toUpperCase() ===
                            facilityDiscountCode.toUpperCase()
                        ) {
                          // Check if discount has expired
                          if (discountEndDate) {
                            const endDate = new Date(discountEndDate);
                            const now = new Date();
                            if (endDate < now) {
                              return {
                                success: false,
                                error: "Coupon or promo code expired",
                              };
                            }
                          }
                          // Facility-specific promo: always 100% off
                          return { success: true, discountedPrice: 0 };
                        }

                        // Dynamic year-month code is sales-only (Nov 2026 "15").
                        if (isSalesOnlyDynamicBypassCode(code, userIsSalesUser())) {
                          return { success: true, discountedPrice: 0 };
                        }

                        // Discount codes from API (same format as former discounts.json)
                        const { codes } = await getDiscountCodes(undefined, code);
                        console.log("codes--------------", codes);
                        const hit = codes.find(
                          (c) => c.name.toUpperCase() === code.toUpperCase()
                        );
                        console.log("hit--------------", hit);
                        if (!hit) {
                          return {
                            success: false,
                            error: "Invalid discount code",
                          };
                        }
                        let discounted = priceValue;
                        const discountType = String(hit.type ?? "").toLowerCase().trim();
                        const percent = Number(
                          (hit as any).discount_percentage ??
                            (hit as any).percentage ??
                            (hit as any).discountPercent ??
                            0
                        );
                        const amount = Number(
                          (hit as any).discount_price ??
                            (hit as any).max_discount_price ??
                            (hit as any).amount ??
                            0
                        );

                        if (
                          discountType === "percentage" ||
                          discountType === "percent" ||
                          (Number.isFinite(percent) && percent > 0)
                        ) {
                          discounted = Math.max(
                            0,
                            priceValue * (1 - percent / 100)
                          );
                        } else if (
                          discountType === "amount" ||
                          (Number.isFinite(amount) && amount > 0)
                        ) {
                          discounted = Math.max(0, priceValue - amount);
                        } else if (discountType === "bypass") {
                          // Show 100% discount for bypass
                          discounted = 0;
                        }
                        return { success: true, discountedPrice: discounted };
                      } catch (e) {
                        return {
                          success: false,
                          error: "Failed to validate code",
                        };
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })()}

      </Container>
    </PricingSection>
  );
}
